/**
 * product-gallery.js
 * Lightweight custom gallery for Safe Builder — no Flickity dependency.
 *
 * Features:
 *  - Thumbnail click → update main image
 *  - Prev/Next arrow navigation
 *  - Keyboard arrow navigation (when gallery is focused)
 *  - Touch swipe on mobile
 *  - Active state on current thumbnail
 *  - Variant image sync via window.SBGallery[productId] API
 *  - Built-in lightbox modal (no external library)
 *  - shopify:section:load re-init
 */

(function () {
  'use strict';

  // ─── Registry so safe-builder.js can call gallery methods ───────────────────
  window.SBGallery = window.SBGallery || {};

  // ─── Shared lightbox singleton ───────────────────────────────────────────────
  var lightbox = null;

  function getLightbox() {
    if (lightbox) return lightbox;

    var overlay = document.createElement('div');
    overlay.className = 'sb-lightbox';
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Image lightbox');
    overlay.innerHTML =
      '<div class="sb-lightbox__backdrop"></div>' +
      '<div class="sb-lightbox__container">' +
        '<button class="sb-lightbox__close" aria-label="Close lightbox" type="button">' +
          '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>' +
        '</button>' +
        '<button class="sb-lightbox__arrow sb-lightbox__arrow--prev" aria-label="Previous image" type="button">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="15 18 9 12 15 6"/></svg>' +
        '</button>' +
        '<button class="sb-lightbox__arrow sb-lightbox__arrow--next" aria-label="Next image" type="button">' +
          '<svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="9 18 15 12 9 6"/></svg>' +
        '</button>' +
        '<div class="sb-lightbox__img-wrap">' +
          '<img class="sb-lightbox__img" src="" alt="">' +
        '</div>' +
        '<div class="sb-lightbox__counter"></div>' +
      '</div>';

    document.body.appendChild(overlay);

    var backdrop  = overlay.querySelector('.sb-lightbox__backdrop');
    var closeBtn  = overlay.querySelector('.sb-lightbox__close');
    var prevBtn   = overlay.querySelector('.sb-lightbox__arrow--prev');
    var nextBtn   = overlay.querySelector('.sb-lightbox__arrow--next');
    var img       = overlay.querySelector('.sb-lightbox__img');
    var counter   = overlay.querySelector('.sb-lightbox__counter');

    var slides    = [];
    var current   = 0;

    function show(index) {
      if (!slides.length) return;
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;
      current = index;

      var slide = slides[current];
      img.src = '';
      img.classList.add('is-loading');
      img.onload = function () { img.classList.remove('is-loading'); };
      img.src = slide.src;
      img.alt = slide.alt || '';

      counter.textContent = slides.length > 1 ? (current + 1) + ' / ' + slides.length : '';

      var hasMultiple = slides.length > 1;
      prevBtn.style.display = hasMultiple ? '' : 'none';
      nextBtn.style.display = hasMultiple ? '' : 'none';
    }

    function open(slideList, startIndex) {
      slides  = slideList;
      show(startIndex || 0);
      overlay.classList.add('is-open');
      document.body.style.overflow = 'hidden';
      closeBtn.focus();
    }

    function close() {
      overlay.classList.remove('is-open');
      document.body.style.overflow = '';
      img.src = '';
      slides = [];
    }

    closeBtn.addEventListener('click', close);
    backdrop.addEventListener('click', close);
    prevBtn.addEventListener('click', function () { show(current - 1); });
    nextBtn.addEventListener('click', function () { show(current + 1); });

    document.addEventListener('keydown', function (e) {
      if (!overlay.classList.contains('is-open')) return;
      if (e.key === 'Escape')     close();
      if (e.key === 'ArrowLeft')  show(current - 1);
      if (e.key === 'ArrowRight') show(current + 1);
    });

    lightbox = { open: open, close: close };
    return lightbox;
  }

  // ─── Init all galleries on the page ─────────────────────────────────────────
  function initAllGalleries() {
    document.querySelectorAll('.sb-gallery').forEach(initGallery);
  }

  function initGallery(gallery) {
    if (gallery._sbInitialized) return;
    gallery._sbInitialized = true;

    var productId   = gallery.dataset.productId;
    var slides      = Array.from(gallery.querySelectorAll('.sb-gallery__slide'));
    var thumbs      = Array.from(gallery.querySelectorAll('.sb-gallery__thumb'));
    var prevBtn     = gallery.querySelector('.sb-gallery__arrow--prev');
    var nextBtn     = gallery.querySelector('.sb-gallery__arrow--next');
    var lightboxOn  = gallery.dataset.lightbox === 'true';

    var current = 0;

    // ── Core: go to slide by index ────────────────────────────────────────────
    function goTo(index) {
      if (index < 0) index = slides.length - 1;
      if (index >= slides.length) index = 0;

      slides[current] && slides[current].classList.remove('is-active');
      thumbs[current] && thumbs[current].classList.remove('is-active');
      thumbs[current] && thumbs[current].removeAttribute('aria-current');

      current = index;

      slides[current] && slides[current].classList.add('is-active');

      if (thumbs[current]) {
        thumbs[current].classList.add('is-active');
        thumbs[current].setAttribute('aria-current', 'true');
        scrollThumbIntoView(thumbs[current]);
      }
    }

    // ── Scroll active thumbnail into view within the strip ───────────────────
    function scrollThumbIntoView(thumb) {
      var strip = thumb.closest('.sb-gallery__thumbs');
      if (!strip) return;
      if (strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: thumb.offsetLeft - strip.clientWidth / 2 + thumb.offsetWidth / 2, behavior: 'smooth' });
      }
      if (strip.scrollHeight > strip.clientHeight) {
        strip.scrollTo({ top: thumb.offsetTop - strip.clientHeight / 2 + thumb.offsetHeight / 2, behavior: 'smooth' });
      }
    }

    // ── Build lightbox slide list from current slides ─────────────────────────
    function buildLightboxSlides() {
      return slides
        .filter(function (s) { return s.dataset.lightboxSrc; })
        .map(function (s) { return { src: s.dataset.lightboxSrc, alt: s.dataset.lightboxAlt || '' }; });
    }

    // ── Lightbox: open at the matching slide index ────────────────────────────
    function openLightboxAt(slideIndex) {
      var lbSlides = buildLightboxSlides();
      if (!lbSlides.length) return;
      // Map gallery slide index → lightbox slide index (only image slides have lightbox data)
      var lbIndex = 0;
      var imageCount = 0;
      for (var i = 0; i < slides.length; i++) {
        if (slides[i].dataset.lightboxSrc) {
          if (i === slideIndex) { lbIndex = imageCount; break; }
          imageCount++;
        }
      }
      getLightbox().open(lbSlides, lbIndex);
    }

    // ── Slide click → lightbox ────────────────────────────────────────────────
    if (lightboxOn) {
      slides.forEach(function (slide, i) {
        if (!slide.dataset.lightboxSrc) return;
        slide.style.cursor = 'zoom-in';
        slide.addEventListener('click', function (e) {
          // Don't open if user was swiping
          if (slide._wasSwiping) return;
          openLightboxAt(i);
        });
      });
    }

    // ── Thumbnail clicks ──────────────────────────────────────────────────────
    thumbs.forEach(function (thumb) {
      thumb.addEventListener('click', function () {
        goTo(parseInt(this.dataset.index, 10));
      });
    });

    // ── Arrow clicks ──────────────────────────────────────────────────────────
    if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

    // ── Keyboard nav (when gallery or a child is focused) ────────────────────
    gallery.setAttribute('tabindex', '0');
    gallery.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   { e.preventDefault(); goTo(current - 1); }
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') { e.preventDefault(); goTo(current + 1); }
    });

    // ── Touch swipe on main image area ────────────────────────────────────────
    var touchStartX = null;
    var touchStartY = null;
    var main = gallery.querySelector('.sb-gallery__main');

    if (main) {
      main.addEventListener('touchstart', function (e) {
        touchStartX = e.touches[0].clientX;
        touchStartY = e.touches[0].clientY;
        slides.forEach(function (s) { s._wasSwiping = false; });
      }, { passive: true });

      main.addEventListener('touchend', function (e) {
        if (touchStartX === null) return;
        var dx = e.changedTouches[0].clientX - touchStartX;
        var dy = e.changedTouches[0].clientY - touchStartY;
        if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 40) {
          slides.forEach(function (s) { s._wasSwiping = true; });
          goTo(dx < 0 ? current + 1 : current - 1);
        }
        touchStartX = null;
        touchStartY = null;
      }, { passive: true });
    }

    // ── Public API — keyed by productId ──────────────────────────────────────
    window.SBGallery[productId] = {
      selectImage: function (mediaId) {
        if (!mediaId) return;
        var target = slides.findIndex(function (s) {
          return s.dataset.mediaId === String(mediaId);
        });
        if (target !== -1) goTo(target);
      },
      goTo: goTo,
      getCurrentIndex: function () { return current; }
    };

    // ── Zoom (hover magnify) ──────────────────────────────────────────────────
    if (gallery.dataset.enableZoom === 'true' && !lightboxOn) {
      initZoom(gallery);
    }
  }

  // ─── Hover zoom ──────────────────────────────────────────────────────────────
  function initZoom(gallery) {
    var main = gallery.querySelector('.sb-gallery__main');
    if (!main) return;

    main.addEventListener('mousemove', function (e) {
      var img = main.querySelector('.sb-gallery__slide.is-active img');
      if (!img) return;
      var rect = main.getBoundingClientRect();
      var x = ((e.clientX - rect.left) / rect.width) * 100;
      var y = ((e.clientY - rect.top) / rect.height) * 100;
      img.style.transformOrigin = x + '% ' + y + '%';
    });

    main.addEventListener('mouseenter', function () {
      var img = main.querySelector('.sb-gallery__slide.is-active img');
      if (img) img.classList.add('is-zoomed');
    });

    main.addEventListener('mouseleave', function () {
      var img = main.querySelector('.sb-gallery__slide.is-active img');
      if (img) { img.classList.remove('is-zoomed'); img.style.transformOrigin = 'center center'; }
    });
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllGalleries);
  } else {
    initAllGalleries();
  }

  // Theme editor re-init
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target.querySelector('.sb-gallery')) {
      e.target.querySelectorAll('.sb-gallery').forEach(function (g) {
        g._sbInitialized = false;
      });
      initAllGalleries();
    }
  });

})();
